import React from 'react';

import { ICartEntry } from 'features/cart/types';
import { calculateEntryTotalPrice } from 'features/cart/utils';
import { StyleSheet } from 'react-native';

import Currency from 'components/currency';
import { Box, Typography } from 'components/ui';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getDesignToken, spacing } from 'styles/constants';

const resolveEntryChildren = (
  entry: ICartEntry,
  currencyFormatter: Function,
  freshNameMap: Map<string, string>
): React.ReactNode[] => {
  if (!entry.children?.length) {
    return [];
  }

  return entry.children.flatMap((child, index) => {
    if (child.children?.length) {
      return resolveEntryChildren(child, currencyFormatter, freshNameMap);
    }

    return (
      <Typography variant="copyTwo" style={styles.entryDescriptionRow} key={`${child.id}-${index}`}>
        {freshNameMap.get(child.id) ?? child.name}
        {child.price?.amount != null ? ` - ${currencyFormatter(child.price.amount)}` : ''}
      </Typography>
    );
  });
};

const MinimalCartEntry = ({
  entry,
  currencyFormatter,
}: {
  entry: ICartEntry;
  currencyFormatter: Function;
}) => {
  const quantity = entry.quantity || 1;
  const amount = calculateEntryTotalPrice(entry);
  const shouldShowPrice = entry.price !== null;

  // Cart entry names are baked in as plain strings at order-placement time and never
  // re-resolved afterward, so a guest who places an order in one language and later switches
  // the app's language keeps seeing the old language on past orders forever. `indexedMenu` is
  // the current, currently-localized unified menu already loaded for this store — prefer its
  // name for each entry/child, only when the lookup actually resolves a value, so a lookup
  // miss (e.g. a discontinued item) can't blank out a name that was already correct.
  const { indexedMenu } = useUnifiedMenuContext();
  const freshNameMap = new Map<string, string>();
  const collectFreshNames = (cartEntry: ICartEntry) => {
    const freshName = indexedMenu.find(cartEntry.id)?.name?.locale;
    if (freshName) {
      freshNameMap.set(cartEntry.id, freshName);
    }
    cartEntry.children?.forEach(collectFreshNames);
  };
  collectFreshNames(entry);

  const name = freshNameMap.get(entry.id) ?? entry.name;
  const cartEntryDescription = resolveEntryChildren(entry, currencyFormatter, freshNameMap);

  return (
    <Box style={styles.item} accessible focusable>
      <Box style={styles.itemHeader}>
        <Typography variant="headerFour" style={styles.itemName}>
          {quantity > 1 && `${quantity}x `}
          {name}
        </Typography>
        <Box style={styles.itemPrice}>
          {shouldShowPrice && (
            <Currency
              amount={amount}
              textProps={{
                ...(amount === 0 && { color: getDesignToken('icon-default') }),
                variant: 'headerFour',
                style: { margin: 0 },
                textAlign: 'right',
              }}
            />
          )}
        </Box>
      </Box>

      {cartEntryDescription}
    </Box>
  );
};

export default MinimalCartEntry;

const styles = StyleSheet.create({
  entryDescriptionRow: {
    lineHeight: 18,
    margin: 0,
    width: '100%',
  },
  item: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: spacing.$1,
    padding: 0,
  },
  itemHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.$1,
    width: '100%',
  },
  itemName: {
    flexBasis: '80%',
    flexShrink: 1,
    margin: 0,
  },
  itemPrice: {
    alignItems: 'flex-end',
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center',
    width: '16.666%',
  },
});
